import PropTypes from 'prop-types';
import { graphql, useFragment, useQueryLoader } from 'react-relay';
import { connect, useDispatch } from 'react-redux';
import { useCheckoutSelector } from '../reducers/root';
import { useEffect, Suspense } from 'react';
import { TABS, PANES } from '../constants/checkoutConstants';
import {
    navigateToPane,
    updateProject,
    setWasNavigatedDueTaxWarningAction,
} from '../actions/checkoutUIActions';
import { isPaneClickable } from '../reducers/checkoutPaneReducer';

import styles from './styles/CheckoutSidebar.scss';

// components
import CheckoutDeposit from '../components/shared/CheckoutDeposit';
import CheckoutBuyerChargeCurrency from '../components/shared/CheckoutBuyerChargeCurrency';
import CheckoutSidebarItemInfo from './CheckoutSidebarItemInfo';
import CheckoutSidebarPromotion from './promotion/CheckoutSidebarPromotion';
import CheckoutSideRailDisclaimer from '../components/shared/CheckoutSideRailDisclaimer';
import { CheckoutFriendRedemption } from '../components/containers/CheckoutFriendRedemption';
import CheckoutTaxExemption from '../components/containers/CheckoutTaxExemption';
import CheckoutTotals from './CheckoutTotals';
import CheckoutSidebarSidemarkPane from './sidemark/CheckoutSidebarSidemarkPane';
import CheckoutSidebarProjectPane from './project/CheckoutSidebarProjectPane';
import CheckoutVatReverseCharge from '../components/containers/CheckoutVatReverseCharge';
import { CheckoutSidebarShimmer } from './CheckoutSidebarShimmer';
import { CheckoutSidebarItemInfoShimmer } from './CheckoutSidebarItemInfoShimmer';
import { query } from 'dibs-customs-messaging/exports/PreloadedCustomsMessaging';

const CheckoutSidebar = props => {
    const {
        viewer: viewerRef,
        transaction: transactionRef,
        item: itemRef,
        buyer: buyerRef,
        isComputing,
        onNavigateToPane,
        currentPane,
        shippingAddressId,
    } = props;

    const dispatch = useDispatch();

    const transaction = useFragment(
        graphql`
            fragment CheckoutSidebar_transaction on Transaction {
                isNegotiable
                ...CheckoutSidebarItemInfo_transaction
                ...CheckoutTotals_transaction
                ...CheckoutSidebarPromotion_transaction
                ...CheckoutDeposit_transaction
                ...CheckoutTaxExemption_transaction
                ...CheckoutSidebarSidemarkPane_transaction
                ...CheckoutVatReverseCharge_transaction
                ...CheckoutBuyerChargeCurrency_transaction
            }
        `,
        transactionRef
    );

    const viewer = useFragment(
        graphql`
            fragment CheckoutSidebar_viewer on Viewer {
                referFriendLink: isEnabledByLocale(featureName: "REFER_FRIEND")
                sidemarkTrade: featureFlag(feature: "checkoutSidemarkTrade")
                ...CheckoutBuyerChargeCurrency_viewer
                ...CheckoutSidebarPromotion_viewer
                ...CheckoutTaxExemption_viewer
                ...CheckoutTaxExemptionCheckbox_viewer
                ...CheckoutSidebarProjectPane_viewer
                ...CheckoutSidebarSidemarkPane_viewer
                ...CheckoutTotals_viewer
                ...CheckoutVatReverseCharge_viewer
            }
        `,
        viewerRef
    );

    const item = useFragment(
        graphql`
            fragment CheckoutSidebar_item on Item {
                ...CheckoutSidebarItemInfo_item
                ...CheckoutTotals_item
                ...CheckoutSidebarPromotion_item
                hasPrivateListing
                serviceId
            }
        `,
        itemRef
    );

    const buyer = useFragment(
        graphql`
            fragment CheckoutSidebar_buyer on User {
                hasAnyTradeStatus: isVerifiedTrade
                ...CheckoutSidebarItemInfo_buyer
                ...CheckoutTotals_buyer
                ...CheckoutTaxExemption_buyer
                ...CheckoutSidebarProjectPane_buyer
                serviceId
                addressBook {
                    addressList {
                        serviceId
                        country
                    }
                }
            }
        `,
        buyerRef
    );

    const shippingAddress = shippingAddressId
        ? (buyer?.addressBook?.addressList || []).find(
              address => address?.serviceId === shippingAddressId
          )
        : undefined;
    const destinationCountry = shippingAddress?.country;

    const onClick = (event, pane) => {
        if (isPaneClickable(pane)) {
            event.preventDefault();
            event.stopPropagation();
            onNavigateToPane({
                pane,
                currentPane,
                isComputing,
                dispatch,
            });
        }
    };

    const isNegotiable = transaction?.isNegotiable;
    const hasAnyTradeStatus = buyer?.hasAnyTradeStatus;
    const referFriendLinkEnabled = viewer?.referFriendLink;
    const showSidemarkPane = hasAnyTradeStatus && viewer?.sidemarkTrade && !item?.hasPrivateListing;
    const showProjectPane = hasAnyTradeStatus && !item?.hasPrivateListing;
    const hasSetUpCheckout = useCheckoutSelector(state => state.ui.hasSetUpCheckout);
    const isAuthChecked = useCheckoutSelector(state => state.ui.authState === 'CHECKED');

    const [queryReference, loadQuery] = useQueryLoader(query);
    const userId = buyer?.serviceId;
    const itemId = item?.serviceId;

    useEffect(() => {
        if (userId && itemId && destinationCountry) {
            loadQuery({
                userId,
                itemId,
                destinationCountry,
            });
        }
    }, [userId, itemId, loadQuery, destinationCountry]);

    return (
        <div className={styles.wrapper} data-tn="checkout-sidebar-container">
            {item && isAuthChecked ? (
                <Suspense fallback={<CheckoutSidebarItemInfoShimmer />}>
                    <CheckoutSidebarItemInfo item={item} buyer={buyer} transaction={transaction} />
                </Suspense>
            ) : (
                <CheckoutSidebarItemInfoShimmer />
            )}
            {isNegotiable && <CheckoutSideRailDisclaimer />}
            {!hasSetUpCheckout || !isAuthChecked ? (
                <CheckoutSidebarShimmer />
            ) : (
                <Suspense fallback={<CheckoutSidebarShimmer />}>
                    <CheckoutTotals
                        item={item}
                        buyer={buyer}
                        viewer={viewer}
                        transaction={transaction}
                        isComputing={isComputing}
                        customsMessagingQueryReference={queryReference}
                    />
                    <CheckoutDeposit transaction={transaction} />
                    <CheckoutBuyerChargeCurrency viewer={viewer} transaction={transaction} />
                    <CheckoutSidebarPromotion
                        viewer={viewer}
                        item={item}
                        transaction={transaction}
                        isComputing={isComputing}
                    />
                    {referFriendLinkEnabled && !hasAnyTradeStatus && <CheckoutFriendRedemption />}
                    {hasAnyTradeStatus && (
                        <>
                            <CheckoutTaxExemption
                                isComputing={isComputing}
                                buyer={buyer}
                                transaction={transaction}
                                viewer={viewer}
                            />
                            <CheckoutVatReverseCharge
                                isComputing={isComputing}
                                transaction={transaction}
                                viewer={viewer}
                            />
                        </>
                    )}

                    {showSidemarkPane && (
                        <CheckoutSidebarSidemarkPane
                            onClick={e => onClick(e, PANES.PANE_SIDEMARK)}
                            transaction={transaction}
                            viewer={viewer}
                        />
                    )}
                    {showProjectPane && (
                        <CheckoutSidebarProjectPane
                            viewer={viewer}
                            buyer={buyer}
                            onClick={e => onClick(e, PANES.PANE_PROJECT)}
                        />
                    )}
                </Suspense>
            )}
        </div>
    );
};

CheckoutSidebar.propTypes = {
    viewer: PropTypes.object,
    transaction: PropTypes.object,
    item: PropTypes.object,
    shippingAddressId: PropTypes.string,
    buyer: PropTypes.object,
    paymentTab: PropTypes.oneOf(Object.values(TABS)),
    isComputing: PropTypes.bool.isRequired,
    onNavigateToPane: PropTypes.func,
    currentPane: PropTypes.oneOf(Object.values(PANES)).isRequired,
};

export default connect(
    ({ pane, pane: { isSummaryOpen, currentPane }, transaction }) => ({
        isSummaryOpen,
        currentPane,
        isClickablePane: paneName => isPaneClickable(pane, paneName),
        shippingAddressId: transaction.shippingAddressId,
    }),
    {
        onUpdateProject: updateProject,
        onNavigateToPane: navigateToPane,
        onSetWasNavigatedDueTaxWarning: setWasNavigatedDueTaxWarningAction,
    }
)(CheckoutSidebar);
